import { useEffect } from 'react';
import { ButtonBox } from '../../../../shared/ui';
import { FeedbackAndReceipt } from '../../../../shared/layouts/FeedbackAndReceipt/FeedbackAndReceipt';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { CancelButton } from '../../../order/components/CancelButton/CancelButton';
import { CancelPage } from '../../../event-log';
import { LoyaltyPaymentButton, useLoyaltyStore } from '../../../loyalty';
import { PaymentButtons } from '../PaymentButtons';
import { PaymentButtonsLoader } from '../PaymentButtonsLoader/PaymentButtonsLoader';

import styles from './PaymentOrFeedback.module.css';

interface PaymentOrFeedbackProps {
    isVisible: boolean;
    screenName: CancelPage;
    /** Replaces the payment buttons with a loader while the entered amount is still being persisted. */
    isPending?: boolean;
    /**
     * Whether pay-with-loyalty is offered for the current selection. Defaults to
     * `true` (free-amount screens). Chip screens pass `false` for any option
     * other than the cheapest one, where redeeming points is not allowed (SWRN-1739).
     */
    isLoyaltyEligible?: boolean;
    children?: React.ReactNode;
}

export const PaymentOrFeedback = ({
    isVisible,
    screenName,
    isPending = false,
    isLoyaltyEligible = true,
    children,
}: PaymentOrFeedbackProps) => {
    const order = useAppStore((store) => store.order);
    const setPaymentButtonsVisible = useLoyaltyStore(
        (store) => store.setPaymentButtonsVisible,
    );

    // Hide the one-time loyalty banner once payment buttons are shown (SWRN-1747).
    useEffect(() => {
        setPaymentButtonsVisible(isVisible);
        return () => setPaymentButtonsVisible(false);
    }, [isVisible, setPaymentButtonsVisible]);

    if (!isVisible) {
        return <FeedbackAndReceipt screenName={screenName} />;
    }

    return (
        <ButtonBox>
            {isPending ? (
                <PaymentButtonsLoader />
            ) : (
                <>
                    <div className={styles.paymentActions}>
                        {isLoyaltyEligible && (
                            <LoyaltyPaymentButton orderId={order.id} />
                        )}
                        <PaymentButtons />
                    </div>
                    {children}
                </>
            )}
            <CancelButton page={screenName} />
        </ButtonBox>
    );
};
